// ─── lazyWithReload.js ────────────────────────────────────────────────────
// Drop-in replacement for React.lazy() that gracefully handles the
// "stale browser bundle" problem after a Vercel deploy.
//
// The bug: when Vite builds, every code-split chunk gets a content-hash
// in its filename (e.g. Admin-CJiYozfL.js). On deploy, Vercel uploads
// the new hashed bundle and removes the old. A user with the OLD index
// bundle still cached in their browser will request lazy chunks by the
// OLD hash on first navigation — which 404s. React surfaces this as
// "Failed to fetch dynamically imported module" and the page goes blank.
//
// The fix: catch chunk-load errors at the lazy() boundary, force a
// one-shot full reload (fetches the fresh index bundle which references
// the fresh chunk hashes), and only show an error to the user if reload
// has already been tried this session (prevents reload loops on a
// genuinely broken deploy).

import { lazy } from 'react'

// Heuristic: detect chunk-load errors by error name + message text.
// React/Vite vary the exact shape across versions; we accept any of the
// common forms rather than requiring an exact match.
function isChunkLoadError(err) {
  if (!err) return false
  if (err.name === 'ChunkLoadError') return true
  const msg = String(err.message || err)
  return /Loading chunk \S+ failed/i.test(msg)
      || /Failed to fetch dynamically imported module/i.test(msg)
      || /error loading dynamically imported module/i.test(msg)
      || /Importing a module script failed/i.test(msg)
}

// sessionStorage flag so we only reload ONCE per session. If the second
// load also fails, we surface the error so the user sees a real failure
// instead of an infinite reload loop.
const RELOAD_FLAG = 'vinarai_chunk_reload_attempted'

export function lazyWithReload(importFn) {
  return lazy(() =>
    importFn().catch(err => {
      if (isChunkLoadError(err) && !sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1')
        // Force a fresh fetch of the index bundle, which holds the new
        // chunk hashes. location.reload() preserves the user's URL so
        // they land back on the same screen they were navigating to.
        window.location.reload()
        // Return a promise that never resolves so React doesn't try to
        // render an undefined component before the reload kicks in.
        return new Promise(() => {})
      }
      // Either not a chunk-load error, or we've already tried reloading
      // and it still failed — let React show the error boundary or
      // unhandled-rejection handler take over.
      throw err
    }),
  )
}

// Clear the reload flag once the app has successfully booted on the
// fresh bundle. Called from App.jsx after the first render — safe to
// invoke without a check, since sessionStorage.removeItem is a no-op
// if the key doesn't exist. This guarantees the user gets ONE retry
// per future stale-bundle situation, not just one per session lifetime.
export function clearChunkReloadFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG)
  } catch {
    // sessionStorage can throw in private browsing or with cookies
    // disabled. The retry-once guard still works (we only set the flag
    // if we can read it back), but the cleanup becomes a no-op.
  }
}
